import { useEffect, useRef } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useCheckoutDialogContext } from '../../guest/components/CheckoutDialog/CheckoutDialog.context';
import type { CheckoutMechanismTypeFragment } from '@graphql/fragments/registry.graphql.generated';
import type { ShippingAddressInput, CheckoutMechanismType } from '@graphql/generated-base';
import { useGetItemCheckoutMechanismsQuery } from '@graphql/queries/registry.graphql.generated';
import { CheckoutFailureCode } from '../components/ShoppingCart/utils/constants';

const JOY_ADDRESS: ShippingAddressInput = {
  name: 'Joy HQ',
  address1: '939 Harrison St',
  city: 'San Francisco',
  state: 'CA',
  postalCode: '94103',
  country: 'United States',
  validated: true,
  countryCode: 'USA',
};

type CheckoutMechanismsProps = Readonly<{
  // actually, it's a RegistryItem id
  productId: Readonly<string>;
  requestedQty?: Readonly<number>;
}>;

export type CheckoutMechanismTypeValue = `${CheckoutMechanismType}`; // eg. 'joyWeb', 'externalNavigation'
export type CheckoutCodeContainer = Pick<CheckoutMechanismTypeFragment, 'checkoutWarningCodes'> & Partial<Pick<CheckoutMechanismTypeFragment, 'type' | 'checkoutFailureCode'>>;

type AggregateCheckoutCodesResult = {
  /** a failure code blocks checkout */
  checkoutFailureCode: CheckoutFailureCode | null;
  isOutOfStock: boolean;
  isLowInventory: boolean;
  isBadAddress: boolean;
  hasOtherProblems: boolean;
};
type BestCheckoutMechanismResult = AggregateCheckoutCodesResult & {
  /** who is sponsoring the checkout (there can be only one) */
  checkoutMechanismSponsor: CheckoutMechanismType | null;
  isDropshipable: boolean;
  hideValueProps: boolean;
};

type MaybeCheckoutMechanismTypeFragment = CheckoutMechanismTypeFragment | null | undefined;
type MaybeCheckoutMechanisms = Array<MaybeCheckoutMechanismTypeFragment> | undefined;
// exported for testing purposes
export function _deriveSponsoringCheckoutMechanism(checkoutMechanisms: MaybeCheckoutMechanisms): MaybeCheckoutMechanismTypeFragment {
  const dropshipMechanism: MaybeCheckoutMechanismTypeFragment = checkoutMechanisms?.find(mechanism => mechanism?.type === 'joyWeb') ?? null;
  const affiliateMechanism: MaybeCheckoutMechanismTypeFragment = checkoutMechanisms?.find(mechanism => mechanism?.type === 'externalNavigation') ?? null;
  const dropshipWarningCodeSet = new Set<string>(dropshipMechanism?.checkoutWarningCodes ?? []);
  const isLowInventory = dropshipWarningCodeSet.has(CheckoutFailureCode.INVENTORY_LOW);

  // here is where we make the decision to always prioritize Dropship;
  //   currently, we believe that a sponsored Affiliate purchase is preferable to a failed Dropship purchase.
  //   it's useful to note that Dropship won't complain about non-SKU-related issues (eg. bad address)
  //   unless it has a suitable SKU for sponsorship -- even one that's considered discontinued.
  //   and keep in mind that we may still not have a viable mechanism when we're done.
  if (dropshipMechanism && dropshipMechanism.checkoutFailureCode !== CheckoutFailureCode.NO_SPONSOR && !isLowInventory) {
    // if it's low inventory / OOS, we should *not* take on Dropship ... yet
    //   to properly support Catalog Affiliate items, which offer both Dropship and Affiliate checkouts.
    //   currently, we believe that a sponsored Affiliate purchase is preferable to an OOS Dropship => Joy Credit checkout
    return dropshipMechanism;
  }
  if (affiliateMechanism && affiliateMechanism.checkoutFailureCode !== CheckoutFailureCode.NO_SPONSOR) {
    // even if it has warnings,
    //   which is not possible at the time of this writing.
    return affiliateMechanism;
  }
  return dropshipMechanism;
}

export const deriveBestCheckoutMechanismResult = (checkoutMechanisms: MaybeCheckoutMechanisms): BestCheckoutMechanismResult => {
  const sponsoringCheckoutMechanism = _deriveSponsoringCheckoutMechanism(checkoutMechanisms);
  const checkoutMechanismSponsor = (sponsoringCheckoutMechanism?.type as CheckoutMechanismType) ?? null;
  const isDropshipable = checkoutMechanismSponsor === 'joyWeb';

  // aggregation
  const hideValueProps = sponsoringCheckoutMechanism?.hideValueProps ?? false;
  // what mechanisms are available?
  const checkoutFailureCode = (sponsoringCheckoutMechanism?.checkoutFailureCode as CheckoutFailureCode) ?? null;
  const checkoutWarningCodes = new Set<string>(sponsoringCheckoutMechanism?.checkoutWarningCodes ?? []);
  const isOutOfStock = checkoutFailureCode === CheckoutFailureCode.INVENTORY_LOW;
  const isLowInventory = checkoutWarningCodes.delete(CheckoutFailureCode.INVENTORY_LOW);
  const isBadAddress = checkoutWarningCodes.delete(CheckoutFailureCode.BAD_ADDRESS);
  const hasOtherProblems = checkoutWarningCodes.size > 0;

  return { checkoutMechanismSponsor, isDropshipable, hideValueProps, checkoutFailureCode, isOutOfStock, isLowInventory, isBadAddress, hasOtherProblems };
};

export const deriveAggregateCheckoutCodesResult = (containers: Array<CheckoutCodeContainer | null | undefined>): AggregateCheckoutCodesResult => {
  const dropshipContainers = <CheckoutCodeContainer[]>containers.filter(container => {
    if (!container) {
      return false;
    }
    const { type, checkoutFailureCode } = container;
    if (type && type !== 'joyWeb') {
      // when containers include a { type }, we only care about the Dropship ones.
      // without codes, we assume they're *all* Dropship.
      return false;
    }
    return checkoutFailureCode !== CheckoutFailureCode.NO_SPONSOR;
  });

  // aggregation
  const checkoutFailureContainer = dropshipContainers.find(container => container.checkoutFailureCode) ?? null;
  const checkoutFailureCode = (checkoutFailureContainer?.checkoutFailureCode as CheckoutFailureCode) ?? null;
  const checkoutWarningCodeSuperset = dropshipContainers.map(container => container.checkoutWarningCodes).flat();
  const checkoutWarningCodes = new Set(checkoutWarningCodeSuperset);
  const isOutOfStock = checkoutFailureCode === CheckoutFailureCode.INVENTORY_LOW;
  const isLowInventory = checkoutWarningCodes.has(CheckoutFailureCode.INVENTORY_LOW);
  const isBadAddress = checkoutWarningCodes.has(CheckoutFailureCode.BAD_ADDRESS);
  const hasOtherProblems = checkoutWarningCodes.size > 0 && !isLowInventory && !isBadAddress;

  return { checkoutFailureCode, isOutOfStock, isLowInventory, isBadAddress, hasOtherProblems };
};

// CHECKOUT MECHANISMS FLOW
// -- First step --
// Checkout mechanisms query (called automatically)
// Variables: product id
// Result: checkoutMechanisms.type === 'joyWeb' ? isDropshipable = true && isOutOfStock = false. No more steps are needed

// -- Second step --
// Check shipping address from registry state
// Result: couple has address ? isDropshipable = false && isOutOfStock = true. No more steps are needed

// -- Third step --
// Refetch query (called through useEffect)
// Variables: product id and a hardcoded address (Joys' address)
// Result: checkoutMechanisms.type !== 'joyWeb' ? isDropshipable = false && isOutOfStock = checkoutMechanisms is empty []. No more steps are needed

// -- Fourth step --
// Refetch query (called through validateAddressWithCheckoutMechanisms)
// Variables: product id and a shipping address entered by the guest
// Result: isDropshipable = checkoutMechanisms.type === 'joyWeb'

export const useCheckoutMechanisms = ({ productId, requestedQty }: CheckoutMechanismsProps) => {
  const isQueryWithJoyAddressAvailable = useRef(true);

  const { registryState } = useCheckoutDialogContext();
  const shippingAddress = registryState?.shippingAddress;

  const { data, loading, refetch, networkStatus } = useGetItemCheckoutMechanismsQuery({
    batchMode: 'fast',
    notifyOnNetworkStatusChange: true,
    // just to be clear -- productId is a RegistryItem ID
    variables: { id: productId, requestedQty },
  });
  const derived = deriveBestCheckoutMechanismResult(data?.registryItem?.productData?.checkoutMechanisms ?? []);
  const { hideValueProps, isDropshipable, isOutOfStock, isLowInventory, isBadAddress, hasOtherProblems, checkoutFailureCode } = derived;

  const validateAddressWithCheckoutMechanisms = async (altAddress: ShippingAddressInput): Promise<boolean> => {
    const queryResult = await refetch({ id: productId, shippingAddress: altAddress, requestedQty });
    const result = deriveBestCheckoutMechanismResult(queryResult?.data?.registryItem?.productData?.checkoutMechanisms);
    return result.isDropshipable && !result.isBadAddress;
  };

  useEffect(() => {
    if (!!data && !isDropshipable && !shippingAddress && isQueryWithJoyAddressAvailable.current) {
      isQueryWithJoyAddressAvailable.current = false;
      refetch({ id: productId, shippingAddress: JOY_ADDRESS, requestedQty });
    }
  }, [data, isDropshipable, isQueryWithJoyAddressAvailable, productId, refetch, shippingAddress, requestedQty]);

  useEffect(() => {
    if (isQueryWithJoyAddressAvailable.current === false) {
      isQueryWithJoyAddressAvailable.current = true;
    }
  }, [productId]);

  return {
    isLoading: loading || networkStatus === NetworkStatus.fetchMore,
    isDropshipable,
    isOutOfStock,
    isBadAddress,
    hasOtherProblems,
    isLowInventory,
    checkoutFailureCode,
    hideValueProps,
    validateAddressWithCheckoutMechanisms,
  };
};
